import React, { useState } from 'react';
import { Brain, Settings, Send, AlertCircle, CheckCircle, Loader2, Trash2, Languages } from 'lucide-react';
import SimpleTranscribe from './SimpleTranscribe';
import useTranscription from '../hooks/useTranscription';
import { createIndustrialContext } from '../utils/contextLoader';

/**
 * IndustrialIntelligencePage Component
 * 
 * A modern page for industrial intelligence analysis with:
 * - Configurable API settings (base URL, agent name, config path)
 * - Text input for breakdown descriptions
 * - API call functionality with modern response display
 * - Error handling and loading states
 * 
 * Follows modern UI/UX design principles with a focus on usability.
 */
const IndustrialIntelligencePage = () => {
  // Configuration state
  const [config, setConfig] = useState({
    baseUrl: 'https://5378c0ea3300.ngrok-free.app',
    agentName: 'short_pilger_classifier_agent',
    configPath: 'config/jk-gemba.yaml',
    sonioxUrl: 'https://voicefuncappv21150.azurewebsites.net',
    translateToEnglish: false, // Any language to English (default: off)
    enableEndTags: true, // Add or remove end tags (default: on)
    // Optimized Soniox settings for noisy industrial environments with Hinglish
    enableHinglish: true, // Enable Hinglish support for mixed-language operators
    enableLanguageIdentification: false, // PERMANENTLY DISABLED to prevent Hindi/Urdu/Arabic confusion
    enableSpeakerDiarization: false, // Usually single operator per recording
    maxNonFinalTokensDurationMs: 6000, // Balanced for noisy shop floor (6 seconds)
    useIndustrialContext: true, // Use optimized industrial context
    excludeArabicLanguages: true // PERMANENT FIX: Exclude Arabic/Urdu from language hints
  });

  // Transcription hook for voice input
  const {
    transcribedText,
    isRecording,
    error: transcriptionError,
    appendText,
    clearText,
    setRecordingState,
    setError: setTranscriptionError,
    clearError: clearTranscriptionError
  } = useTranscription();

  // Response and UI state
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle transcription updates
  const handleTranscriptionUpdate = (newText) => {
    appendText(newText);
    clearTranscriptionError(); // Clear any previous transcription errors
    setError(null); // Clear any API errors when receiving new text
  };

  // Handle transcription errors
  const handleTranscriptionError = (err) => {
    setTranscriptionError(err);
  };

  // Handle recording state changes
  const handleRecordingStateChange = (recording) => {
    setRecordingState(recording);
  };

  // Handle manual text input changes
  const handleTextChange = (e) => {
    const newText = e.target.value;
    // Clear existing text and set new text
    clearText();
    if (newText) {
      appendText(newText);
    }
  };

  // Clear all text
  const handleClearText = () => {
    clearText();
    setError(null);
    clearTranscriptionError();
  };

  // Test connection to the API
  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus(null);

    try {
      // Validate URL format
      if (!config.baseUrl || !config.baseUrl.startsWith('http')) {
        throw new Error('Invalid API URL format');
      }

      console.log('🔍 Testing connection to:', config.baseUrl);
      console.log('🔍 User Agent:', navigator.userAgent);
      console.log('🔍 Online status:', navigator.onLine);

      // Step 1: Test basic connectivity with a simple request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ Request timed out after 15 seconds');
        controller.abort();
      }, 15000); // 15 second timeout

      console.log('🚀 Making test request...');
      const startTime = Date.now();

      const response = await fetch(config.baseUrl, {
        method: 'GET',
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal,
        mode: 'cors'
      });

      const duration = Date.now() - startTime;
      clearTimeout(timeoutId);

      console.log(`📊 Response received in ${duration}ms`);
      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));
      console.log('📊 Response URL:', response.url);
      console.log('📊 Response type:', response.type);

      if (response.ok) {
        // Try to read a bit of the response
        const responseText = await response.text();
        console.log('📊 Response preview:', responseText.substring(0, 200));

        setConnectionStatus({
          success: true,
          message: `✅ Connection successful! Server responded with status ${response.status}`,
          details: `Server is reachable at ${config.baseUrl} (${duration}ms response time)`
        });
      } else {
        const responseText = await response.text().catch(() => 'Unable to read response');
        console.log('⚠️ Error response body:', responseText);

        setConnectionStatus({
          success: false,
          message: `⚠️ Server responded with status ${response.status}`,
          details: `The server is reachable but returned: ${response.statusText}. Response: ${responseText.substring(0, 100)}`
        });
      }
    } catch (err) {
      console.error('❌ Connection test error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        cause: err.cause
      });

      let errorMessage = '❌ Connection failed';
      let details = '';

      if (err.name === 'AbortError') {
        errorMessage = '❌ Connection timeout';
        details = 'The server did not respond within 15 seconds. The ngrok tunnel may be inactive or the server is down.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = '❌ Network fetch failed';
        details = `Cannot reach ${config.baseUrl}. Possible causes:
        • ngrok tunnel is inactive or expired
        • Server is not running
        • Network connectivity issues
        • CORS policy blocking the request
        • Firewall or proxy blocking the connection`;
      } else if (err.message.includes('NetworkError')) {
        errorMessage = '❌ Network error';
        details = `Network error when trying to reach ${config.baseUrl}. Check your internet connection and server status.`;
      } else if (err.message.includes('CORS')) {
        errorMessage = '❌ CORS error';
        details = 'Cross-origin request blocked. The server needs to allow requests from this domain.';
      } else {
        details = `Error: ${err.message}. Check browser console for more details.`;
      }

      setConnectionStatus({
        success: false,
        message: errorMessage,
        details: details
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Handle API call
  const handleAnalyze = async () => {
    if (!transcribedText.trim()) {
      setError('Please enter a breakdown description or record your voice');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Validate URL format
      if (!config.baseUrl || !config.baseUrl.startsWith('http')) {
        throw new Error('Invalid API URL. Please check the configuration.');
      }

      const formData = new FormData();
      formData.append('agent_name', config.agentName);
      formData.append('input', `"${transcribedText}"`);
      formData.append('config_path', config.configPath);
      formData.append('raw_output', 'True');

      const apiUrl = `${config.baseUrl}/worker/upload`;
      console.log('🚀 Making API request to:', apiUrl);
      console.log('📤 Request data:', {
        agent_name: config.agentName,
        input: transcribedText,
        config_path: config.configPath,
        raw_output: 'True'
      });
      console.log('🌐 Online status:', navigator.onLine);

      // Add timeout and better error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ API request timed out after 30 seconds');
        controller.abort();
      }, 30000); // 30 second timeout

      const startTime = Date.now();
      const apiResponse = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'Accept': 'application/json, text/plain, */*'
        },
        signal: controller.signal,
        mode: 'cors'
      });

      const duration = Date.now() - startTime;
      clearTimeout(timeoutId);

      console.log(`📊 API Response received in ${duration}ms`);
      console.log('📊 API Response status:', apiResponse.status);
      console.log('📊 API Response headers:', Object.fromEntries(apiResponse.headers.entries()));
      console.log('📊 API Response URL:', apiResponse.url);
      console.log('📊 API Response type:', apiResponse.type);

      if (!apiResponse.ok) {
        // Try to get error details from response
        let errorDetails = '';
        try {
          const errorText = await apiResponse.text();
          errorDetails = errorText ? ` - ${errorText}` : '';
        } catch (e) {
          // Ignore error reading response body
        }
        throw new Error(`API request failed: ${apiResponse.status} ${apiResponse.statusText}${errorDetails}`);
      }

      const responseText = await apiResponse.text();
      console.log('Raw API response:', responseText);

      // Handle the response which might contain ```json wrapper
      let jsonData;
      try {
        // Try to parse as direct JSON first
        jsonData = JSON.parse(responseText);
      } catch (e) {
        // If that fails, try to extract JSON from markdown code block
        const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          jsonData = JSON.parse(jsonMatch[1]);
        } else {
          throw new Error(`Invalid JSON response format. Raw response: ${responseText.substring(0, 200)}...`);
        }
      }

      // Check if the response indicates an error
      if (jsonData.success === false) {
        throw new Error(jsonData.error || 'API returned an error response');
      }

      setResponse(jsonData);
    } catch (err) {
      console.error('❌ API call error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        cause: err.cause,
        url: `${config.baseUrl}/worker/upload`
      });

      // Provide more specific error messages
      let errorMessage = err.message || 'An error occurred while analyzing the input';

      if (err.name === 'AbortError') {
        errorMessage = '⏰ Request timed out. The server may be unavailable or overloaded. Try again in a few moments.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = `🔌 Connection error: Unable to reach the server at ${config.baseUrl}.

Possible solutions:
• Check if the ngrok URL is active (ngrok tunnels expire)
• Verify your internet connection
• Ensure the server is running and accessible
• Try using the "Test" button to diagnose the connection

Technical details: ${err.message}`;
      } else if (err.message.includes('NetworkError')) {
        errorMessage = `🌐 Network error when trying to reach ${config.baseUrl}. Check your internet connection and server status.`;
      } else if (err.message.includes('CORS')) {
        errorMessage = '🚫 Cross-origin request blocked. The server needs to allow requests from this domain.';
      } else if (err.message.includes('API request failed')) {
        errorMessage = `🔴 Server error: ${err.message}. The server is reachable but returned an error.`;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Render suggestion card
  const renderSuggestion = (suggestion, index) => (
    <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-mono text-blue-300 bg-blue-900/30 px-2 py-1 rounded">
              {suggestion.code}
            </span>
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${
                suggestion.confidence_score >= 0.8 ? 'bg-green-400' :
                suggestion.confidence_score >= 0.6 ? 'bg-yellow-400' : 'bg-red-400'
              }`} />
              <span className="text-xs text-gray-300">
                {Math.round(suggestion.confidence_score * 100)}%
              </span>
            </div>
          </div>
          <h4 className="text-white font-medium mb-2">{suggestion.suggested_label}</h4>
          <p className="text-sm text-gray-300 mb-2">
            <strong>Action:</strong> {suggestion.acceptance_action}
          </p>
          {suggestion.mapping_to_existing_ids && suggestion.mapping_to_existing_ids.length > 0 && (
            <div className="text-xs text-gray-400">
              <strong>Mapped IDs:</strong> {suggestion.mapping_to_existing_ids.join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Industrial Intelligence
            </h1>
            <p className="text-xl text-purple-100">
              AI-powered breakdown analysis and classification
            </p>
          </div>

          {/* Configuration Panel */}
          <div className="mb-6">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span>Configuration</span>
            </button>
            
            {showConfig && (
              <div className="mt-4 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Industrial Intelligence API URL
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={config.baseUrl}
                        onChange={(e) => handleConfigChange('baseUrl', e.target.value)}
                        className="flex-1 p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        placeholder="https://api.example.com"
                      />
                      <button
                        onClick={testConnection}
                        disabled={isTestingConnection}
                        className="px-4 py-3 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {isTestingConnection ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Settings className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">
                          {isTestingConnection ? 'Testing...' : 'Test'}
                        </span>
                      </button>
                    </div>
                    {connectionStatus && (
                      <div className={`mt-2 p-3 rounded-lg text-sm ${
                        connectionStatus.success
                          ? 'bg-green-900/20 border border-green-500/30 text-green-300'
                          : 'bg-red-900/20 border border-red-500/30 text-red-300'
                      }`}>
                        <div className="font-medium">{connectionStatus.message}</div>
                        {connectionStatus.details && (
                          <div className="text-xs mt-1 opacity-80">{connectionStatus.details}</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Rayosense Voice API URL
                    </label>
                    <input
                      type="text"
                      value={config.sonioxUrl}
                      onChange={(e) => handleConfigChange('sonioxUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://voice-api.example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Agent Name
                    </label>
                    <input
                      type="text"
                      value={config.agentName}
                      onChange={(e) => handleConfigChange('agentName', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="agent_name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Config Path
                    </label>
                    <input
                      type="text"
                      value={config.configPath}
                      onChange={(e) => handleConfigChange('configPath', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="config/path.yaml"
                    />
                  </div>
                </div>

                {/* Voice Transcription Settings */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center space-x-2">
                    <Languages className="w-4 h-4" />
                    <span>Voice Transcription Settings</span>
                  </h4>

                  {/* Basic Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="translateToEnglish"
                        checked={config.translateToEnglish}
                        onChange={(e) => handleConfigChange('translateToEnglish', e.target.checked)}
                        className="w-4 h-4 text-purple-600 bg-black/20 border-white/10 rounded focus:ring-purple-500/50 focus:ring-2"
                      />
                      <label htmlFor="translateToEnglish" className="text-sm text-white">
                        Translate any language to English
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="enableEndTags"
                        checked={config.enableEndTags}
                        onChange={(e) => handleConfigChange('enableEndTags', e.target.checked)}
                        className="w-4 h-4 text-purple-600 bg-black/20 border-white/10 rounded focus:ring-purple-500/50 focus:ring-2"
                      />
                      <label htmlFor="enableEndTags" className="text-sm text-white">
                        Enable end tags
                      </label>
                    </div>
                  </div>

                  {/* Industrial Environment Optimizations */}
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h5 className="text-sm font-medium text-blue-300 mb-3">Industrial Environment Optimizations</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableHinglish"
                          checked={config.enableHinglish}
                          onChange={(e) => handleConfigChange('enableHinglish', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="enableHinglish" className="text-sm text-white">
                          Enable Hinglish support (Hindi + English)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3 opacity-50">
                        <input
                          type="checkbox"
                          id="enableLanguageIdentification"
                          checked={false}
                          disabled={true}
                          className="w-4 h-4 text-gray-600 bg-black/20 border-white/10 rounded cursor-not-allowed"
                        />
                        <label htmlFor="enableLanguageIdentification" className="text-sm text-gray-400">
                          Language identification per token (DISABLED - Permanent Fix)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableSpeakerDiarization"
                          checked={config.enableSpeakerDiarization}
                          onChange={(e) => handleConfigChange('enableSpeakerDiarization', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="enableSpeakerDiarization" className="text-sm text-white">
                          Speaker diarization (multiple operators)
                        </label>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="useIndustrialContext"
                          checked={config.useIndustrialContext}
                          onChange={(e) => handleConfigChange('useIndustrialContext', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-black/20 border-white/10 rounded focus:ring-blue-500/50 focus:ring-2"
                        />
                        <label htmlFor="useIndustrialContext" className="text-sm text-white">
                          Use industrial context (technical terms)
                        </label>
                      </div>
                    </div>

                    {/* Permanent Hindi Script Fix */}
                    {config.enableHinglish && (
                      <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                          <label className="text-sm text-green-200 font-medium">
                            Hindi Script Protection: ACTIVE
                          </label>
                        </div>
                        <p className="text-xs text-green-300">
                          ✅ Permanent fix applied: Arabic/Urdu languages excluded from hints, language identification disabled.
                          Hindi will always appear in Devanagari script (है, ठीक, नहीं) instead of Arabic script (ہ، ٹھیک، نہیں).
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Advanced Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Response Time (ms) - Higher for noisy environments
                      </label>
                      <select
                        value={config.maxNonFinalTokensDurationMs}
                        onChange={(e) => handleConfigChange('maxNonFinalTokensDurationMs', parseInt(e.target.value))}
                        className="w-full p-2 bg-black/20 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      >
                        <option value={3000}>3000ms (Quiet environment)</option>
                        <option value={6000}>6000ms (Noisy shop floor - Recommended)</option>
                        <option value={9000}>9000ms (Very noisy environment)</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <div className="text-xs text-gray-400">
                        <div className="font-medium text-white mb-1">Current Settings:</div>
                        <div>Languages: {config.enableHinglish ? 'English + Hindi' : 'English only'}</div>
                        <div>Response: {config.maxNonFinalTokensDurationMs}ms</div>
                        <div>Context: {config.useIndustrialContext ? 'Industrial' : 'General'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Input Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-white mb-2">
                Breakdown Description
              </label>

              {/* Controls Section */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {/* Transcribe Component with Industrial Optimizations */}
                  <SimpleTranscribe
                    onTranscriptionUpdate={handleTranscriptionUpdate}
                    onError={handleTranscriptionError}
                    onRecordingStateChange={handleRecordingStateChange}
                    size="lg"
                    sonioxUrl={config.sonioxUrl}
                    translateToEnglish={config.translateToEnglish}
                    enableEndTags={config.enableEndTags}
                    // Industrial environment optimizations - PERMANENT HINDI SCRIPT FIX
                    languageHints={config.enableHinglish ? ["hi", "en"] : ["en"]} // Hindi first, NO Arabic/Urdu languages
                    enableLanguageIdentification={false} // PERMANENTLY DISABLED to prevent script confusion
                    enableSpeakerDiarization={config.enableSpeakerDiarization}
                    maxNonFinalTokensDurationMs={config.maxNonFinalTokensDurationMs}
                    context={config.useIndustrialContext ? createIndustrialContext(config.enableHinglish) : null}
                    onContextLoaded={(context) => {
                      console.log(`Industrial context loaded: ${context.length} characters`);
                    }}
                    onContextError={(error) => {
                      console.error('Context loading error:', error);
                    }}
                  />

                  {/* Status Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isRecording ? 'text-red-300' : 'text-gray-300'
                    }`}>
                      {isRecording ? 'Recording...' : 'Ready'}
                    </span>
                  </div>
                </div>

                {/* Clear Button */}
                <button
                  onClick={handleClearText}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              </div>

              <div className="space-y-4">
                <textarea
                  value={transcribedText}
                  onChange={handleTextChange}
                  placeholder={
                    isRecording
                      ? config.enableHinglish
                        ? "Listening... Speak in English, Hindi, or mixed (Hinglish)!"
                        : "Listening... Start speaking!"
                      : config.enableHinglish
                        ? "Enter breakdown description (e.g., 'Oil leakage' or 'Tel leak kar raha hai') or click the microphone to record..."
                        : "Enter breakdown description (e.g., Oil leakage) or click the microphone to record..."
                  }
                  className="w-full h-32 p-4 bg-black/20 border border-white/10 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
                />
                <div className="flex justify-center">
                  <button
                    onClick={handleAnalyze}
                    disabled={isLoading || !transcribedText.trim()}
                    className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    <span>{isLoading ? 'Analyzing...' : 'Analyze'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {(error || transcriptionError) && (
              <div className="mb-6 space-y-2">
                {error && (
                  <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300">{error}</span>
                  </div>
                )}
                {transcriptionError && (
                  <div className="p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-300">Transcription Error: {transcriptionError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Response Display */}
            {response && (
              <div className="space-y-6">
                {/* Request Meta */}
                {response.request_meta && (
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span>Request Information</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Raw Note:</span>
                        <p className="text-white mt-1">{response.request_meta.raw_note}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Machine:</span>
                        <p className="text-white mt-1">{response.request_meta.machine}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Timestamp:</span>
                        <p className="text-white mt-1">{response.request_meta.timestamp}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {response.suggestions && response.suggestions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">
                      Classification Suggestions ({response.suggestions.length})
                    </h3>
                    <div className="space-y-4">
                      {response.suggestions.map((suggestion, index) => renderSuggestion(suggestion, index))}
                    </div>
                  </div>
                )}

                {/* Meta Recommendation */}
                {response.meta_recommendation && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-blue-300 font-medium mb-2">Recommendation</h4>
                    <p className="text-blue-200">{response.meta_recommendation}</p>
                  </div>
                )}
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-purple-300 mb-2">How to use:</h3>
                <ul className="text-sm text-purple-200 space-y-1">
                  <li>• Enter a breakdown description manually or click the microphone to record your voice</li>
                  <li>• The microphone turns red while recording - click again to stop</li>
                  <li>• Supports mixed Hindi-English (Hinglish) for Indian operators</li>
                  <li>• Hindi Script Protection: ACTIVE - Hindi always appears in Devanagari (है, ठीक, नहीं)</li>
                  <li>• Optimized for noisy industrial environments with 6-second response time</li>
                  <li>• Click "Analyze" to get AI-powered classification suggestions</li>
                  <li>• Review suggestions with confidence scores - higher scores are more reliable</li>
                  <li>• Use the Clear button to reset and start over</li>
                </ul>
              </div>

              {/* Sample Phrases for Testing */}
              {config.enableHinglish && (
                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-300 mb-2">Sample phrases to test (Hinglish support enabled):</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="font-medium text-blue-200 mb-1">English Technical:</div>
                      <ul className="text-blue-100 space-y-1">
                        <li>• "Gearbox bearing is overheating"</li>
                        <li>• "Roll chip detected on mandrel"</li>
                        <li>• "Hydraulic pump cavitation sound"</li>
                        <li>• "VFD trip due to earth fault"</li>
                      </ul>
                    </div>
                    <div>
                      <div className="font-medium text-blue-200 mb-1">Hinglish Mixed:</div>
                      <ul className="text-blue-100 space-y-1">
                        <li>• "Bearing garam ho raha hai"</li>
                        <li>• "Gear ka daant toot gaya"</li>
                        <li>• "Tel leak kar raha hai"</li>
                        <li>• "Motor rumbling hai"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Configuration Status */}
              <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-green-300 mb-2">Current Optimization Status:</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${config.enableHinglish ? 'bg-green-400' : 'bg-gray-400'}`} />
                    <span className="text-green-200">Hinglish: {config.enableHinglish ? 'ON' : 'OFF'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${config.useIndustrialContext ? 'bg-green-400' : 'bg-gray-400'}`} />
                    <span className="text-green-200">Context: {config.useIndustrialContext ? 'Industrial' : 'General'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${config.maxNonFinalTokensDurationMs >= 6000 ? 'bg-green-400' : 'bg-yellow-400'}`} />
                    <span className="text-green-200">Noise: {config.maxNonFinalTokensDurationMs >= 6000 ? 'Optimized' : 'Standard'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${config.enableHinglish ? 'bg-green-400' : 'bg-gray-400'}`} />
                    <span className="text-green-200">Script: {config.enableHinglish ? 'Hindi Protected' : 'Standard'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="text-green-200">Lang ID: DISABLED (Permanent Fix)</span>
                  </div>
                </div>

                {/* Hindi Script Protection Status */}
                {config.enableHinglish && (
                  <div className="mt-3 p-2 bg-green-900/30 border border-green-500/50 rounded text-xs text-green-200">
                    ✅ Hindi Script Protection: ACTIVE - Hindi will always appear in Devanagari script (है, ठीक, नहीं).
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustrialIntelligencePage;
